import type { ComponentType } from 'react'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import TrendingUp from '@mui/icons-material/TrendingUp'
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline'
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined'
import Autorenew from '@mui/icons-material/Autorenew'
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined'
import type { SxProps } from '@mui/material'
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material'
import InfoTooltip from 'components/InfoTooltip'
import GLOBAL from 'modules/global'
import { useGetReservationDashboardQuery } from 'store/dashboard'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'
import { STATUS_COLORS } from './chartDefaults'

type SvgIconComponent = ComponentType<{ sx?: SxProps }>

const STATUS_ICONS: Record<string, SvgIconComponent> = {
  Completed: CheckCircleOutline,
  Cancel: WarningAmberOutlined,
  Cancelled: WarningAmberOutlined,
  'Reservation On Going': Autorenew,
  'Service On Process': PeopleAltOutlined
}

const KpiCard = ({
  label,
  value,
  subtitle,
  color,
  icon: Icon,
  loading,
  error,
  onRefresh
}: {
  label: string
  value?: string
  subtitle?: string
  color: string
  icon: SvgIconComponent
  loading?: boolean
  error?: boolean
  onRefresh?: () => void
}) => (
  <Card sx={{ bgcolor: color, border: 'none', boxShadow: 'none', borderRadius: 2, height: '100%' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 600 }}>
          {label}
        </Typography>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon sx={{ fontSize: 16, color: '#fff' }} />
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', pt: 1 }}>
          <CircularProgress size={24} sx={{ color: '#fff' }} />
        </Box>
      ) : error || value == null ? (
        <NoDataAvailable onRefresh={onRefresh} />
      ) : (
        <>
          <Typography variant='h5' sx={{ color: '#fff', fontWeight: 700, mt: 1 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {subtitle}
            </Typography>
          )}
        </>
      )}
    </CardContent>
  </Card>
)

const DashboardSummaryCards = () => {
  const { startDate, endDate } = useTimeRange()

  const {
    data: reservationDashboard,
    isLoading: dashboardLoading,
    isError: dashboardError,
    refetch: refetchDashboard
  } = useGetReservationDashboardQuery({ startDate, endDate })

  const visitDealerRate = reservationDashboard?.visitDealerRate
  const totalBooking = reservationDashboard?.totalBooking ?? 0
  const visitCount = visitDealerRate != null ? Math.round((totalBooking * visitDealerRate) / 100) : undefined
  const nonVisitCount = visitCount != null ? totalBooking - visitCount : undefined

  const dealerVisitStats = [
    {
      key: 'visit',
      label: 'VISIT DEALER RATE',
      tooltip: 'Percentage of service bookings where customers visit the dealer for service.',
      value: visitDealerRate != null ? `${visitDealerRate.toFixed(1)}% (${visitCount?.toLocaleString('id-ID')})` : '-'
    },
    {
      key: 'non-visit',
      label: 'NON VISIT DEALER RATE',
      tooltip: "Percentage of service bookings completed through Mobile Service at the customer's location.",
      value:
        visitDealerRate != null
          ? `${(100 - visitDealerRate).toFixed(1)}% (${nonVisitCount?.toLocaleString('id-ID')})`
          : '-'
    }
  ]

  return (
    <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label='Total Booking'
            value={reservationDashboard?.totalBooking.toLocaleString('id-ID')}
            subtitle='100% of Total'
            color='#002C5F'
            icon={DescriptionOutlined}
            loading={dashboardLoading}
            error={dashboardError}
            onRefresh={refetchDashboard}
          />
        </Grid>

        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label='Service Revenue'
            value={`Rp${GLOBAL.formatCurrency(reservationDashboard?.totalRevenue ?? 0)}`}
            subtitle='100% of Total'
            color='#4CAF50'
            icon={TrendingUp}
            loading={dashboardLoading}
            error={dashboardError}
            onRefresh={refetchDashboard}
          />
        </Grid>

        {dashboardLoading ? (
          <Grid item xs={12} sm={4} md={8} sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} sx={{ color: '#002C5F' }} />
          </Grid>
        ) : dashboardError || !reservationDashboard ? (
          <Grid item xs={12} sm={4} md={8}>
            <NoDataAvailable onRefresh={refetchDashboard} />
          </Grid>
        ) : (
          (reservationDashboard.statusSummary ?? []).map((item) => (
            <Grid item xs={6} sm={4} md={2} key={item.status}>
              <KpiCard
                label={item.status}
                value={item.count.toLocaleString('id-ID')}
                subtitle={`${item.percentage.toFixed(1)}% of Total`}
                color={STATUS_COLORS[item.status] ?? '#607D8B'}
                icon={STATUS_ICONS[item.status] ?? DescriptionOutlined}
              />
            </Grid>
          ))
        )}
      </Grid>

      <Card sx={{ bgcolor: '#002C5F', border: 'none', boxShadow: 'none', borderRadius: 2, mb: 3, spacing: 6 }}>
        <CardContent sx={{ p: 3, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <Grid container justifyContent='center'>
            {dealerVisitStats.map((stat) => (
              <Grid item xs={12} sm={6} key={stat.key} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600, letterSpacing: 1 }}>
                      {stat.label}
                    </Typography>
                    <InfoTooltip title={stat.tooltip} iconColor='rgba(255,255,255,0.7)' variant='light' />
                  </Box>
                  <Typography variant='h4' sx={{ color: '#fff', fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}

export default DashboardSummaryCards
